import React, { Fragment, useEffect } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setPageOptions } from 'reducers/layout';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
import { setTableParams } from 'reducers/tableParams';
import { openModal } from 'reducers/modals';
// endpoints
import {
  getPigGroupTreatmentProtocols,
  updatePigGroupTreatmentProtocol,
  deletePigGroupTreatmentProtocol,
  bulkUpdatePigGroupTreatmentProtocols,
  bulkDeletePigGroupTreatmentProtocols,
} from 'endpoints/treatmentProtocols';
// components
import VerticalAligner from 'components/VerticalAligner';
import DotSeparator from 'components/DotSeparator/DotSeparator';
import DropdownButton from 'components/DropdownButton/DropdownButton';
import Panel from 'components/Panel';
import SearchBox from 'components/SearchBox';
import Preloader from 'components/Preloader';
import TableFilter from 'components/TableFilter';
import NothingBox from 'components/NothingBox';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';
import { CustomColumn } from 'components/DataTable/Columns';
import StatusBadge, { CombinedBadge } from 'components/StatusBadge/StatusBadge';
import DataTable from 'components/DataTable/DataTable';
import DeleteTreatmentProtocolModal from '../../components/DeleteTreatmentProtocolModal';
import CombinedTreatmentProtocolModal from '../../components/CombinedTreatmentProtocolModal';
import Button from 'components/Button';
// hooks
import useDataTable, { tableParamsSelector }  from 'hooks/useDataTable';
import { useDropdownActions } from 'hooks/useDropdownContext';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import cn from 'classnames';
// constants
import { tableNames } from 'utils/constants';
// styles
import styles from '../../TreatmentProtocols.module.scss';

const GroupTreatmentProtocols = ({
  params: {
    groupId,
    farmId,
  },
  pigGroup,
  location: {
    pathname
  },
  router,
  setBreadcrumbs,
  setPageOptions,
  tableParams,
  setTableParams,
  openModal,
}) => {
  const {
    data: { resources, isLoading, meta },
    fetchData,
    onPageChange,
    onPerPageChange,
    onSearchChange,
    onStatusChange,
  } = useDataTable((params) => getPigGroupTreatmentProtocols(groupId, params), {
    setTableParams: (params) => setTableParams(tableNames.pigGroupTreatmentProtocols, params),
    tableParams,
  });
  const { openDropdown } = useDropdownActions();
  const { page, per_page, search, status } = tableParams;
  const { total, stats } = meta;

  const paginationProps = {
    onPageChange,
    onPerPageChange,
    totalItems: total,
    currentPage: page,
    perPage: per_page,
  };

  useEffect(() => {
    fetchData(tableParams);
    setPageOptions({ backLink: `/treatment-protocols-farms/${farmId}/groups` });
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.treatmentProtocolFarms" />,
        path: '/treatment-protocols-farms',
      },
      {
        label: pigGroup?.farm?.name || '',
        path: `/treatment-protocols-farms/${farmId}/groups`,
      },
      {
        label: pigGroup?.name || '',
        useLabelAsMobileTitle: true,
      },
    ]);
  }, [pigGroup]);

  const getEditPath = (treatmentProtocolId) => `${pathname}/${treatmentProtocolId}/edit`;
  const getCopyIds = (rowData) => rowData.protocols.map(({ id }) => id);

  // A combined entry stands for the copies of several sow units; every action on it explains first
  // which copies of this group it touches
  const openCombinedModal = (rowData, action, onConfirm, onEditCopy) => {
    openModal(
      <CombinedTreatmentProtocolModal
        action={action}
        treatmentProtocol={rowData}
        groupName={pigGroup?.name}
        onConfirm={onConfirm}
        onEditCopy={onEditCopy}
      />
    );
  };

  const onDeleteTreatmentProtocol = async (treatmentProtocol) => {
    try {
      if (treatmentProtocol.combined) {
        await bulkDeletePigGroupTreatmentProtocols(groupId, getCopyIds(treatmentProtocol));
      } else {
        await deletePigGroupTreatmentProtocol(groupId, treatmentProtocol.id);
      }
      await fetchData(tableParams);
    } catch (err) {
      toastResponseErrors(err);
    }
  };

  const handleDeleteTreatmentProtocol = (treatmentProtocol) => {
    if (treatmentProtocol.combined) {
      openCombinedModal(treatmentProtocol, 'delete', () => onDeleteTreatmentProtocol(treatmentProtocol));
      return;
    }
    openModal(
      <DeleteTreatmentProtocolModal
        treatmentProtocol={treatmentProtocol}
        onDeleteTreatmentProtocol={() => onDeleteTreatmentProtocol(treatmentProtocol)}
      />
    );
  };

  const onTreatmentProtocolStatusChange = async (rowData, isActive) => {
    try {
      if (rowData.combined) {
        await bulkUpdatePigGroupTreatmentProtocols(groupId, getCopyIds(rowData), { active: isActive });
      } else {
        await updatePigGroupTreatmentProtocol(groupId, rowData.id, { active: isActive });
      }
    } catch (error) {
      toastResponseErrors(error);
    }
    fetchData(tableParams);
  };

  const handleStatusChange = (rowData) => {
    const isActive = !rowData.active;
    if (rowData.combined) {
      const action = isActive ? 'enable' : 'disable';
      openCombinedModal(rowData, action, () => onTreatmentProtocolStatusChange(rowData, isActive));
      return;
    }
    onTreatmentProtocolStatusChange(rowData, isActive);
  };

  // Identical copies are edited together, copies that differ one by one
  const handleEdit = (rowData) => {
    if (!rowData.combined) {
      router.push(getEditPath(rowData.id));
      return;
    }
    openCombinedModal(
      rowData,
      'edit',
      () => router.push(`${getEditPath(rowData.id)}?sync_combined=true`),
      (copy) => router.push(getEditPath(copy.id)),
    );
  };

  const handleOpenDropdown = (e, rowData) => {
    const options = [
      {
        label: <FormattedMessage id="general.button.delete" />,
        onClick: () => handleDeleteTreatmentProtocol(rowData),
        hide: rowData.active,
      },
    ];

    openDropdown(e, options, { isScrollIgnored: true });
  };

  const renderNameColumn = (rowData, rowIndex) => (
    <CustomColumn>
      <div className={styles['treatment-protocol-name-column']}>
        <VerticalAligner>
          <span className="hide-for-large semibold">
            <FormattedMessage id="component.dataTable.headers.name" />
            <DotSeparator />
          </span>
          {rowData.combined ? (
            // Each copy keeps its own profile, for its products or to pause it alone
            <span>
              {rowData.protocols.map((protocol, index) => (
                <Fragment key={protocol.id}>
                  {index > 0 && ' / '}
                  <Link className="primary" to={`${pathname}/${protocol.id}`}>{protocol.name}</Link>
                </Fragment>
              ))}
              <CombinedBadge className="ml-10" />
            </span>
          ) : (
            <Link className="primary" to={`${pathname}/${rowData.id}`}>
              {rowData.name}
            </Link>
          )}
        </VerticalAligner>
        {!rowData.active && (
          <i
            data-name={`${rowData.name}-${rowData.id}-${rowIndex}`}
            className={cn('fa fa-dots-three-horizontal hide-for-large', styles['dropdown-icon'])}
            onClick={(e) => handleOpenDropdown(e, rowData)}
          />
        )}
      </div>
    </CustomColumn>
  );

  const renderStatusColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.status" />}>
      <StatusBadge status={rowData.active ? 'active' : 'disabled'} />
    </CustomColumn>
  );

  const getSourceFarmName = (rowData) => (rowData.combined
    ? rowData.protocols.map(({ source_farm }) => source_farm?.name).filter(Boolean).join(', ')
    : rowData.farm_treatment_protocol?.farm?.name);

  const renderSourceFarm = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.sourceFarm" />}>
      {getSourceFarmName(rowData) || <FormattedMessage id="general.notAvail" />}
    </CustomColumn>
  );

  const renderActionsColumn = (rowData, rowIndex) => {
    const options = [
      {
        label: <FormattedMessage id="general.button.edit" />,
        onClick: () => handleEdit(rowData),
      },
      {
        label: <FormattedMessage id={`general.button.${rowData.active ? 'disable' : 'enable'}`} />,
        onClick: () => handleStatusChange(rowData),
      },
      {
        label: <FormattedMessage id="general.button.delete" />,
        onClick: () => handleDeleteTreatmentProtocol(rowData),
        hide: rowData.active,
      }
    ];
    if (!options.length) return null;

    const firstOption = options[0];
    const restOptions = options.slice(1);

    return (
      <div className="collapsible-value button-column">
        <DropdownButton
          idKey={`${rowData.id}-${rowIndex}`}
          label={firstOption.label}
          buttonType="small light-gray"
          onClick={firstOption.onClick}
          dropDownData={restOptions}
          customClass="show-for-large"
          wide
        />
        <Link onClick={firstOption.onClick} className="button light hide-for-large">
          {firstOption.label}
        </Link>
        <Button className="hide-for-large" onClick={() => handleStatusChange(rowData)}>
          <FormattedMessage id={`general.button.${rowData.active ? 'disable' : 'enable'}`} />
        </Button>
      </div>
    );
  };

  const columns = [
    {
      label: <FormattedMessage id="component.dataTable.headers.name" />,
      flex: '1 1 130px',
      renderer: renderNameColumn,
    },
    {
      label: <FormattedMessage id="component.dataTable.headers.status" />,
      flex: '1 1 100px',
      renderer: renderStatusColumn,
    },
    {
      label: <FormattedMessage id="general.sourceFarm" />,
      flex: '1 1 130px',
      renderer: renderSourceFarm,
    },
    {
      label: '',
      flex: '0 0 135px',
      renderer: renderActionsColumn,
      fixed: true,
    },
  ];

  const treatmentProtocolsTableFilters = [
    { label: <FormattedMessage id="general.allTreatmentProtocols" />, value: '' },
    { label: <FormattedMessage id="general.status.active" />, value: 'active' },
    { label: <FormattedMessage id="general.status.disabled" />, value: 'disabled' },
  ];

  return (
    <section className="small-12 column">
      <Panel>
        <Panel.Heading title={<FormattedMessage id="general.treatmentProtocols" />}>
          <SearchBox initialValue={search} onChange={onSearchChange} />
        </Panel.Heading>
        <Panel.Body noPadding>
          <Preloader isActive={isLoading} />
          <TableFilter
            filters={treatmentProtocolsTableFilters}
            activeFilter={status}
            onFilterChange={onStatusChange}
            stats={stats}
            className="ph-10"
          >
            <Link
              to={`/treatment-protocols-farms/${farmId}/groups/${groupId}/treatment-protocols/create`}
              className="button small primary wider"
            >
              <FormattedMessage id="general.button.addTreatmentProtocol" />
            </Link>
          </TableFilter>
          <DataTable
            data={resources}
            columns={columns}
            scrollable
            isLoading={isLoading}
            paginationProps={paginationProps}
            rowKey="row_key"
          />
          <NothingBox
            filter={status}
            search={search}
            itemsName="treatmentProtocols"
            display={!resources.length}
            isLoading={isLoading}
          >
            <h2 className="lighter semibold">
              <FormattedMessage id="component.nothingBox.noTreatmentProtocols" />
            </h2>
          </NothingBox>
        </Panel.Body>
      </Panel>
    </section>
  );
};

GroupTreatmentProtocols.propTypes = {
  params: T.object.isRequired,
  pigGroup: T.object.isRequired,
  router: T.object.isRequired,
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  tableParams: T.object.isRequired,
  location: T.object.isRequired,
  setTableParams: T.func.isRequired,
  openModal: T.func.isRequired,
};

export default connect(
  (state) => ({
    pigGroup: state.profiles.pigGroup.data,
    tableParams: tableParamsSelector(state, tableNames.pigGroupTreatmentProtocols),
  }), {
    setPageOptions,
    setBreadcrumbs,
    setTableParams,
    openModal,
  }
)(GroupTreatmentProtocols);
